import React, { useEffect, useState } from "react";
import {
  Dialog,
  IconButton,
  Select,
  MenuItem,
  Box,
  Typography,
  useMediaQuery,
  Chip,
  Stack,
  Fade,
  Paper,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import { useTheme } from "@mui/material/styles";
import { Filter } from "lucide-react";
import { analystCollections, analystCollectionNames } from "../../../api/api";
import AddToWatchlistDialog from "../../IndividualStocks/Widgets/AddToWatchlistDialog";
import { getCurrencySymbol } from "../../../utils/currency";

/* ================= HELPERS ================= */
const sym = getCurrencySymbol();

const fmtL = (n, d = 2) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

const formatPrice = (val) => {
  if (val === null || val === undefined) return "—";
  return `${sym}${fmtL(val)}`;
};

const formatScore = (val) => {
  if (val === null || val === undefined) return "—";
  return fmtL(val);
};

const scoreToBucketKey = (score) => {
  const num = parseFloat(score);
  if (num >= 71.07) return "strongBuy";
  if (num >= 63.25) return "buy";
  if (num >= 57.15) return "hold";
  if (num >= 50.38) return "sell";
  return "strongSell";
};

const ratingToBucketKey = (rating) => {
  const map = {
    "Strong Buy": "strongBuy",
    "Buy": "buy",
    "Hold": "hold",
    "Sell": "sell",
    "Strong Sell": "strongSell",
  };
  return map[rating] ?? null;
};

const gradeToKey = (grade) => {
  const map = {
    "A+": "aPlus", "A": "a", "A-": "aMinus",
    "B+": "bPlus", "B": "b", "B-": "bMinus",
    "C+": "cPlus", "C": "c", "C-": "cMinus",
    "D+": "dPlus", "D": "d", "D-": "dMinus",
    "E+": "ePlus", "E": "e", "E-": "eMinus",
    "F": "f",
  };
  return map[grade] ?? "unknown";
};

const prettifyCollection = (name) =>
  name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

/* ================= STYLES ================= */

const thStyle = {
  padding: "12px 16px",
  fontSize: "var(--font-size-sm)",
  fontWeight: "var(--font-weight-bold)",
  color: "var(--color-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  textAlign: "left",
  position: "sticky",
  top: 0,
  background: "var(--color-surface)",
  backdropFilter: "blur(8px)",
  borderBottom: "2px solid var(--color-success)",
  opacity: 0.95,
  zIndex: 1,
};

/* ================= COMPONENT ================= */

const TopStocksDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";

  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("");
  const [collectionTitle, setCollectionTitle] = useState("");
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredRow, setHoveredRow] = useState(null);

  /* Watchlist state */
  const [watchlistOpen, setWatchlistOpen] = useState(false);

  /* All tickers in the current collection */
  const allTickers = stocks.map((s) => s.ticker);

  /* ================= API ================= */

  useEffect(() => {
    if (!open) return;
    const fetchCollections = async () => {
      setLoading(true);
      const res = await analystCollectionNames();
      if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
        const market = sessionStorage.getItem("selectedMarket") ?? "US";

        const filtered =
          market === "ALL"
            ? res.data
            : res.data.filter((c) => c.market_code === market);

        setCollections(filtered);

        const preferred =
          market === "TR"
            ? (filtered.find((c) => c.collection === "Analyst Pick") ?? filtered[0])
            : (filtered.find((c) => c.collection === "top-10-stocks") ?? filtered[0]);

        if (!preferred) {
          setCollections([]);
          setStocks([]);
          setLoading(false);
          return;
        }

        setSelectedCollection(preferred.collection);
        fetchCollectionStocks(preferred.collection);
      } else {
        setCollections([]);
        setStocks([]);
        setLoading(false);
      }
    };
    fetchCollections();
  }, [open]);

  const fetchCollectionStocks = async (collection) => {
    setLoading(true);
    const res = await analystCollections(collection);
    if (res?.success && res.data?.picks) {
      setStocks(res.data.picks);
      setCollectionTitle(res.data.title || prettifyCollection(collection));
    } else {
      setStocks([]);
      setCollectionTitle("");
    }
    setLoading(false);
  };

  const handleWatchlistClose = (success) => {
    setWatchlistOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            height: isMobile ? "100vh" : "85vh",
            borderRadius: isMobile ? 0 : 3,
            overflow: "hidden",
            bgcolor: "var(--color-bg-primary)",
            backgroundImage: "none",
          },
        }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        {/* ================= HEADER ================= */}
        <Box sx={{ position: "sticky", top: 0, zIndex: 10 }}>
          <Box
            sx={{
              bgcolor: "var(--color-surface)",
              backdropFilter: "blur(16px)",
              px: 3,
              py: 2.5,
              borderBottom: "1px solid var(--color-border-primary)",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box display="flex" flexDirection="column">
                <Typography variant="title" color="var(--color-text-primary)">
                  Analyst Picks
                </Typography>
                {/* <Typography variant="paragraph" color="var(--color-text-muted)" mt={0.5}>
                  Each stock is selected using a disciplined framework combining valuation, growth momentum, profitability strength, and risk factors.
                </Typography> */}
              </Box>
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  color: "var(--color-text-primary)",
                  bgcolor: "var(--color-surface-hover)",
                  "&:hover": {
                    bgcolor: "var(--color-surface-active)",
                    transform: "rotate(90deg)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {/* ================= TOOLBAR ================= */}
          <Box
            px={3}
            py={2.5}
            sx={{
              borderBottom: "1px solid var(--color-border-primary)",
              backgroundColor: "var(--color-surface)",
            }}
          >
            {/* Collection label row */}
            <Stack direction="row" alignItems="center" gap={1.5} mb={1.5}>
              <Filter size={16} color="var(--color-success)" />
              <Typography
                variant="paragraph"
                color="var(--color-text-muted)"
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                Collection
              </Typography>
            </Stack>

            {/* Collection selector + Add to Watchlist button on same row */}
            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
              <Paper
                elevation={0}
                sx={{
                  maxWidth: 300,
                  flex: "0 0 auto",
                  backgroundColor: "var(--color-surface)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 2,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "var(--color-success)",
                    boxShadow: "var(--shadow-md)",
                  },
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel
                    sx={{
                      ...theme.typography.tableHeader,
                      color: "var(--color-text-muted)",
                      "&.Mui-focused": { color: "var(--color-success)" },
                    }}
                  >
                    Collection
                  </InputLabel>
                  <Select
                    value={selectedCollection}
                    label="Collection"
                    onChange={(e) => {
                      setSelectedCollection(e.target.value);
                      fetchCollectionStocks(e.target.value);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      ...theme.typography.tableHeader,
                      fontWeight: "var(--font-weight-semibold)",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    {collections.map((col) => (
                      <MenuItem key={col.collection} value={col.collection}>
                        <Typography variant="tableHeader">
                          {col.title || prettifyCollection(col.collection)}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>

              {/* ── Add to Watchlist ── */}
              <Button
                disabled={loading || allTickers.length === 0}
                startIcon={<BookmarkAddOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={() => setWatchlistOpen(true)}
                sx={{
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  textTransform: "none",
                  px: 2.5,
                  py: 1,
                  borderRadius: "var(--radius-lg)",
                  border: "1.5px solid rgba(59,130,246,0.4)",
                  bgcolor: "rgba(59,130,246,0.08)",
                  color: "var(--color-info)",
                  transition: "all var(--transition-fast)",
                  "&:hover": {
                    bgcolor: "rgba(59,130,246,0.15)",
                    borderColor: "var(--color-info)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(59,130,246,0.2)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.4,
                    color: "var(--color-text-muted)",
                    borderColor: "var(--color-border-secondary)",
                    bgcolor: "transparent",
                  },
                }}
              >
                Add to Watchlist
              </Button>
            </Stack>

            {/* Chips row */}
            {selectedCollection && (
              <Stack direction="row" gap={1} mt={2} flexWrap="wrap">
                <Chip
                  label={prettifyCollection(selectedCollection)}
                  size="small"
                  sx={(t) => ({
                    ...t.typography.chipText,
                    bgcolor: "var(--color-success-light)",
                    color: "var(--color-success)",
                    height: 24,
                  })}
                />
                {/* <Chip
                  label={`${stocks.length} picks`}
                  size="small"
                  sx={(t) => ({
                    ...t.typography.chipText,
                    bgcolor: "var(--color-info-light)",
                    color: "var(--color-info)",
                    height: 24,
                  })}
                /> */}
              </Stack>
            )}
          </Box>
        </Box>

        {/* ================= TABLE ================= */}
        <Box flex={1} overflow="auto" sx={{ bgcolor: "var(--color-bg-secondary)" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                {/* <th style={thStyle}>#</th> */}
                {/* Rank Number */}
                <th style={thStyle}>#</th>
                <th style={thStyle}>Ticker</th>
                <th style={thStyle}>Company</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Price</th>
                <th style={{ ...thStyle, textAlign: "right" }}>FA Score</th>
                <th style={{ ...thStyle, textAlign: "center" }}>FA Rating</th>
                <th style={{ ...thStyle, textAlign: "right" }}>FA Grade</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "400px",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          border: "3px solid var(--color-border-primary)",
                          borderTopColor: "var(--color-success)",
                          animation: "spin 1s linear infinite",
                          "@keyframes spin": {
                            "0%": { transform: "rotate(0deg)" },
                            "100%": { transform: "rotate(360deg)" },
                          },
                        }}
                      />
                      <Typography variant="tableHeader" color="var(--color-text-muted)">
                        Loading analyst picks...
                      </Typography>
                    </Box>
                  </td>
                </tr>
              ) : stocks.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "200px",
                      }}
                    >
                      <Typography variant="tableHeader" color="var(--color-text-muted)">
                        No picks found for this collection.
                      </Typography>
                    </Box>
                  </td>
                </tr>
              ) : (
                // stocks.map((row, idx) => (
                [...stocks]
                  .reduce((acc, row) => {
                    const existing = acc.find((r) => r.ticker === row.ticker);
                    if (!existing || row.overall_score > existing.overall_score) {
                      return [...acc.filter((r) => r.ticker !== row.ticker), row];
                    }
                    return acc;
                  }, [])
                  .sort((a, b) => (b.overall_score ?? 0) - (a.overall_score ?? 0))
                  .map((row, idx) => (
                    <Fade in key={row.id} timeout={300} style={{ transitionDelay: `${idx * 30}ms` }}>
                      <tr
                        onMouseEnter={() => setHoveredRow(row.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{
                          background:
                            hoveredRow === row.id
                              ? "var(--color-surface-hover)"
                              : "var(--color-surface)",
                          borderBottom: "1px solid var(--color-border-primary)",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {/* Rank */}
                        <td style={{ padding: "14px 16px" }}>
                          <Typography
                            variant="paragraph"
                            sx={{
                              fontWeight: "var(--font-weight-semibold)",
                              color: "var(--color-text-muted)",
                            }}
                          >
                            {idx + 1}
                          </Typography>
                        </td>

                        {/* Ticker */}
                        <td style={{ padding: "14px 16px" }}>
                          <Typography
                            variant="paragraph"
                            sx={{
                              fontWeight: "var(--font-weight-semibold)",
                              color: hoveredRow === row.id ? "var(--color-success)" : "var(--color-primary)",
                            }}
                          >
                            {row.ticker}
                          </Typography>
                        </td>

                        {/* Company */}
                        <td style={{ padding: "14px 16px" }}>
                          <Typography
                            variant="paragraph"
                            sx={{ fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}
                          >
                            {row.company_name}
                          </Typography>
                        </td>

                        {/* Price */}
                        <td style={{ padding: "14px 16px", textAlign: "right" }}>
                          <Typography
                            variant="paragraph"
                            sx={{ fontWeight: "var(--font-weight-semibold)", color: "var(--color-text-primary)" }}
                          >
                            {formatPrice(row.current_price)}
                          </Typography>
                        </td>

                        {/* FA Score */}
                        <td style={{ padding: "14px 16px", textAlign: "right" }}>
                          <Chip
                            label={formatScore(row.overall_score)}
                            size="small"
                            sx={(t) => ({
                              ...t.typography.chipText,
                              bgcolor: t.palette.buckets[scoreToBucketKey(row.overall_score)],
                              color: t.palette.text.inverse,
                              fontWeight: "var(--font-weight-bold)",
                              height: 24,
                              minWidth: 60,
                            })}
                          />
                        </td>

                        {/* Rating */}
                        <td style={{ padding: "14px 16px", textAlign: "center" }}>
                          <Typography
                            variant="chipText"
                            sx={(t) => ({
                              fontWeight: "var(--font-weight-bold)",
                              // color: t.palette.buckets[scoreToBucketKey(row.overall_score)],
                              color: t.palette.buckets[ratingToBucketKey(row.overall_rating) ?? scoreToBucketKey(row.overall_score)],
                            })}
                          >
                            {row.overall_rating || "—"}
                          </Typography>
                        </td>

                        {/* Grade */}
                        <td style={{ padding: "14px 16px", textAlign: "right" }}>
                          <Chip
                            label={row.overall_grade || row.overall_rating || "—"}
                            size="small"
                            sx={(t) => ({
                              ...t.typography.chipText,
                              bgcolor: t.palette.grades[gradeToKey(row.overall_grade ?? row.overall_rating)],
                              color: t.palette.text.inverse,
                              fontWeight: "var(--font-weight-bold)",
                              height: 24,
                              minWidth: 40,
                            })}
                          />
                        </td>
                      </tr>
                    </Fade>
                  ))
              )}
            </tbody>
          </table>
        </Box>

        {/* ================= FOOTER ================= */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "var(--color-surface)",
            backdropFilter: "blur(16px)",
            borderTop: "1px solid var(--color-border-primary)",
            px: 3,
            py: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="var(--color-text-muted)">
              {/* {loading ? "Loading..." : `${stocks.length} analyst picks`} */}
            </Typography>
            {collectionTitle && (
              <Typography variant="caption" color="var(--color-text-muted)">
                {/* Ordered by rank */}
                Ordered by FA Score
              </Typography>
            )}
          </Stack>
        </Box>
      </Dialog>

      {/* ================= WATCHLIST DIALOG ================= */}
      <AddToWatchlistDialog
        open={watchlistOpen}
        ticker={null}
        tickers={allTickers}
        onClose={handleWatchlistClose}
      />
    </>
  );
};

export default TopStocksDialog;